import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { usePdfPreview } from '../hooks/usePdfPreview';
import PDFPreviewModal from '../components/PDFPreviewModal';
import { 
  getFacturesAPayer, 
  getArchivesMensuelles, 
  marquerFacturePayee,
  getCategoriesFactures,
  analyzeInvoiceEntreprise,
  uploadFactureEntreprise,
  getFactureEntrepriseDownloadUrl,
  getFactureDownloadUrl,
  getArchiveExportUrl,
  deleteFactureEntreprise,
  updateFactureArtisan,
  updateFactureEntreprise,
  getFraisARembourser,
  rembourserFrais,
} from '../services/api';
import { 
  Receipt, 
  Plus, 
  Search, 
  Download,
  Check,
  Clock,
  AlertTriangle,
  FolderArchive,
  Upload,
  X,
  Eye,
  Trash2,
  FileCheck,
  Building,
  Calendar,
  Euro,
  Info,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Undo2,
  Pencil,
  Send,
  Settings,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const FournisseursPage = () => {
  const [activeTab, setActiveTab] = useState('a_payer');
  const [facturesAPayer, setFacturesAPayer] = useState([]);
  const [archives, setArchives] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fraisARembourser, setFraisARembourser] = useState(null);
  const loadFrais = () => getFraisARembourser().then((r) => setFraisARembourser(r.data)).catch(() => setFraisARembourser(null));
  useEffect(() => { loadFrais(); }, []);
  const handleRembourserGroupe = async (g) => {
    if (!window.confirm(`Marquer comme remboursé ${g.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € à ${g.auteur_nom || 'ce salarié'} ?`)) return;
    try {
      await rembourserFrais(g.notes.map((n) => n.id), g.trajets.map((t) => t.id));
      toast.success('Frais remboursés');
      loadFrais();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };
  const [search, setSearch] = useState('');
  const [expandedArchive, setExpandedArchive] = useState(null);
  const [archiveSearch, setArchiveSearch] = useState(''); // global search across all months
  const [monthSearches, setMonthSearches] = useState({}); // per-month search keyed by month_key
  const { previewState: pdfPreview, openPreview: openPdfPreview, closePreview: closePdfPreview } = usePdfPreview();
  
  // Modal upload facture entreprise
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [analyzingInvoice, setAnalyzingInvoice] = useState(false);
  const [uploadingFacture, setUploadingFacture] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  
  // Modal edit facture
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFacture, setEditingFacture] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [newFacture, setNewFacture] = useState({
    fournisseur_nom: '',
    montant_ht: '',
    montant_ttc: '',
    date_facture: '',
    numero_facture: '',
    categorie: 'Divers',
    commentaire: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [facturesRes, archivesRes, categoriesRes] = await Promise.all([
        getFacturesAPayer().catch(() => ({ data: [] })),
        getArchivesMensuelles().catch(() => ({ data: [] })),
        getCategoriesFactures().catch(() => ({ data: { categories: [] } }))
      ]);
      setFacturesAPayer(facturesRes.data || []);
      setArchives(archivesRes.data || []);
      setCategories(categoriesRes.data?.categories || []);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handlePayFacture = async (type, id) => {
    if (!window.confirm('Marquer cette facture comme payée ?')) return;
    try {
      await marquerFacturePayee(type, id);
      toast.success('Facture marquée comme payée');
      loadData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleDeleteFacture = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await deleteFactureEntreprise(id);
      toast.success('Facture supprimée');
      loadData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleDeleteArchivedFacture = async (type, id, nom) => {
    if (!window.confirm(`Supprimer définitivement la facture "${nom}" ?`)) return;
    try {
      if (type === 'entreprise') {
        await deleteFactureEntreprise(id);
      } else {
        // For artisan invoices, we need a different approach - just delete the record
        const { deleteFactureArtisan } = await import('../services/api');
        await deleteFactureArtisan(id);
      }
      toast.success('Facture supprimée');
      loadData();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleArchive = (monthKey) => {
    setExpandedArchive(expandedArchive === monthKey ? null : monthKey);
  };

  // Edit facture
  const openEditModal = (facture) => {
    setEditingFacture({
      ...facture,
      fournisseur_nom: facture.fournisseur_nom || facture.artisan_nom || facture.nom || '',
      montant_ht: facture.montant_ht ? String(facture.montant_ht) : '',
      montant_ttc: facture.montant_ttc ? String(facture.montant_ttc) : '',
      date_facture: facture.date_facture || '',
      numero_facture: facture.numero_facture || '',
      categorie: facture.categorie || 'Divers'
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingFacture) return;
    
    setSavingEdit(true);
    try {
      const updateData = {
        montant_ht: parseFloat(editingFacture.montant_ht) || 0,
        montant_ttc: parseFloat(editingFacture.montant_ttc) || 0,
        date_facture: editingFacture.date_facture,
        numero_facture: editingFacture.numero_facture
      };
      
      if (editingFacture.type === 'entreprise') {
        // For enterprise invoices, we can also update name and category
        updateData.fournisseur_nom = editingFacture.fournisseur_nom;
        updateData.categorie = editingFacture.categorie;
        await updateFactureEntreprise(editingFacture.id, updateData);
      } else {
        // For artisan invoices
        updateData.artisan_nom = editingFacture.fournisseur_nom;
        await updateFactureArtisan(editingFacture.id, updateData);
      }
      
      toast.success('Facture mise à jour');
      setShowEditModal(false);
      setEditingFacture(null);
      loadData();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
      console.error(e);
    } finally {
      setSavingEdit(false);
    }
  };

  // Drag & Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!analyzingInvoice) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (analyzingInvoice) return;
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        handleAnalyzeInvoice(file);
      } else {
        toast.error('Seuls les fichiers PDF sont acceptés');
      }
    }
  };

  const handleAnalyzeInvoice = async (file) => {
    if (!file) return;
    
    setSelectedFile(file);
    setAnalyzingInvoice(true);
    toast.info('Analyse IA en cours...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await analyzeInvoiceEntreprise(formData);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setNewFacture(prev => ({
          ...prev,
          fournisseur_nom: data.fournisseur_nom || prev.fournisseur_nom,
          numero_facture: data.numero_facture || prev.numero_facture,
          date_facture: data.date_facture || prev.date_facture,
          montant_ht: data.montant_ht ? String(data.montant_ht) : prev.montant_ht,
          montant_ttc: data.montant_ttc ? String(data.montant_ttc) : prev.montant_ttc,
          categorie: data.categorie || prev.categorie
        }));
        toast.success('Facture analysée ! Vérifiez les données.');
      } else {
        toast.warning('Analyse partielle - complétez manuellement');
      }
    } catch (e) {
      toast.error('Erreur d\'analyse - remplissez manuellement');
      console.error(e);
    } finally {
      setAnalyzingInvoice(false);
    }
  };

  const handleSaveFacture = async (force = false) => {
    if (!selectedFile || !newFacture.fournisseur_nom || !newFacture.montant_ht) {
      toast.error('Fichier PDF, fournisseur et montant HT requis');
      return;
    }
    
    setUploadingFacture(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('fournisseur_nom', newFacture.fournisseur_nom);
      formData.append('montant_ht', newFacture.montant_ht);
      if (newFacture.montant_ttc) formData.append('montant_ttc', newFacture.montant_ttc);
      if (newFacture.date_facture) formData.append('date_facture', newFacture.date_facture);
      if (newFacture.numero_facture) formData.append('numero_facture', newFacture.numero_facture);
      formData.append('categorie', newFacture.categorie);
      if (newFacture.commentaire) formData.append('commentaire', newFacture.commentaire);
      if (force) formData.append('force', 'true');
      
      await uploadFactureEntreprise(formData);
      toast.success('Facture enregistrée !');
      closeModal();
      loadData();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 409 && detail && detail.code === 'duplicate_facture') {
        const existing = detail.existing || {};
        const dateInfo = existing.date_facture || existing.date_paiement || '?';
        const fileInfo = existing.fichier_renomme || '?';
        const confirmMsg = `⚠️ ${detail.message}\n\nFacture existante :\n  • ${fileInfo}\n  • Date : ${dateInfo}\n  • N° : ${existing.numero_facture || '—'}\n\nVeux-tu quand même enregistrer cette nouvelle facture ?`;
        if (window.confirm(confirmMsg)) {
          await handleSaveFacture(true);
        }
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'Erreur upload');
    } finally {
      setUploadingFacture(false);
    }
  };

  const closeModal = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setNewFacture({
      fournisseur_nom: '',
      montant_ht: '',
      montant_ttc: '',
      date_facture: '',
      numero_facture: '',
      categorie: 'Divers',
      commentaire: ''
    });
  };

  const formatMoney = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const isUrgent = (facture) => {
    const dateStr = facture.date_validation || facture.date_facture || facture.date_creation;
    if (!dateStr) return false;
    const factureDate = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.floor((today - factureDate) / (1000 * 60 * 60 * 24));
    return diffDays > 30;
  };

  const getDaysOld = (facture) => {
    const dateStr = facture.date_validation || facture.date_facture || facture.date_creation;
    if (!dateStr) return 0;
    const factureDate = new Date(dateStr);
    const today = new Date();
    return Math.floor((today - factureDate) / (1000 * 60 * 60 * 24));
  };

  const filteredFactures = facturesAPayer.filter(f => {
    const matchesSearch =
      (f.fournisseur_nom || f.artisan_nom || '').toLowerCase().includes(search.toLowerCase()) ||
      (f.projet_nom || '').toLowerCase().includes(search.toLowerCase()) ||
      (f.categorie || '').toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    return true;
  });

  const totalAPayer = facturesAPayer.reduce((sum, f) => sum + (f.montant_ht || 0), 0);
  const urgentCount = facturesAPayer.filter(isUrgent).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="fournisseurs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Fournisseurs MAAG</h1>
          <p className="text-slate-500 mt-1">Gestion centralisée des factures à payer</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              // Open Aliantis Connect in a popup positioned on the right half of the screen
              // so MAAG stays visible on the left. User then drags PDFs from downloads into it.
              const w = Math.min(900, Math.floor(window.screen.availWidth * 0.55));
              const h = Math.floor(window.screen.availHeight * 0.92);
              const left = window.screen.availWidth - w - 10;
              const top = 30;
              window.open(
                'https://aliantis-connect.aliantis.net',
                'aliantis',
                `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
              );
            }}
            data-testid="open-aliantis-btn"
            title="Ouvre Aliantis Connect à côté pour glisser-déposer tes factures manuellement"
            className="flex items-center px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
          >
            <Send className="w-4 h-4 mr-2 text-indigo-500" />
            Ouvrir Aliantis
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            data-testid="add-facture-entreprise-btn"
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Facture entreprise
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-amber-600 mb-1">À payer</p>
              <p className="text-2xl font-bold text-amber-900">{formatMoney(totalAPayer)}</p>
              <p className="text-xs text-amber-600 mt-1">{facturesAPayer.length} facture(s)</p>
            </div>
            <Receipt className="w-10 h-10 text-amber-400" />
          </div>
        </div>
        
        {urgentCount > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-red-600 mb-1">Urgentes (&gt; 30j)</p>
                <p className="text-2xl font-bold text-red-900">{urgentCount}</p>
                <p className="text-xs text-red-600 mt-1">facture(s) en retard</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
          </div>
        )}
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-green-600 mb-1">Archives</p>
              <p className="text-2xl font-bold text-green-900">{archives.length}</p>
              <p className="text-xs text-green-600 mt-1">mois disponibles</p>
            </div>
            <FolderArchive className="w-10 h-10 text-green-400" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('a_payer')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'a_payer' 
              ? 'border-indigo-600 text-indigo-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Receipt className="w-4 h-4 inline mr-2" />
          À payer ({facturesAPayer.length})
        </button>
        <button
          onClick={() => setActiveTab('archives')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'archives' 
              ? 'border-indigo-600 text-indigo-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <FolderArchive className="w-4 h-4 inline mr-2" />
          Archives mensuelles
        </button>
      </div>

      {/* Tab: À payer */}
      {activeTab === 'a_payer' && (
        <div>
          {fraisARembourser && fraisARembourser.groupes.length > 0 && (
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="frais-a-rembourser">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-amber-900 flex items-center gap-2"><Receipt className="w-4 h-4" /> Notes de frais & indemnités km à rembourser</h3>
                <span className="font-mono font-bold text-amber-900">{fraisARembourser.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
              </div>
              <div className="space-y-2">
                {fraisARembourser.groupes.map((g) => (
                  <div key={g.auteur_id} className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-lg border border-amber-100 px-3 py-2" data-testid={`frais-groupe-${g.auteur_id}`}>
                    <div className="text-sm">
                      <span className="font-medium text-slate-800">{g.auteur_nom || 'Salarié'}</span>
                      <span className="text-slate-500"> · {g.notes.length} note(s) de frais · {g.trajets.length} trajet(s)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-semibold">{g.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                      <button onClick={() => handleRembourserGroupe(g)} className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700" data-testid={`frais-rembourser-${g.auteur_id}`}>Marquer remboursé</button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-amber-700 mt-2">Avances perso validées et trajets validés dans le module Frais. Détail et PDF comptable : menu « Frais ».</p>
            </div>
          )}
          {/* Search + filter chips */}
          <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher par fournisseur, projet ou catégorie..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full md:w-96 h-10 pl-10 pr-4 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {filteredFactures.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <Check className="w-12 h-12 mx-auto mb-4 text-green-400" />
              <p className="text-slate-500 font-medium">Aucune facture à payer</p>
              <p className="text-sm text-slate-400 mt-1">Toutes les factures sont réglées !</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Fournisseur</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-500">Montant HT</th>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase text-slate-500">Âge</th>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredFactures.map((f) => {
                    const urgent = isUrgent(f);
                    const days = getDaysOld(f);
                    return (
                      <tr key={`${f.type}-${f.id}`} className={`hover:bg-slate-50 ${urgent ? 'bg-red-50/50' : ''}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">{f.fournisseur_nom || f.artisan_nom}</p>
                          <div className="flex items-center gap-1 flex-wrap mt-1">
                            {f.categorie && (
                              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                {f.categorie}
                              </span>
                            )}
                            {f.sent_to_comptable_at && (
                              <span
                                className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                                title={`Envoyée au comptable le ${f.sent_to_comptable_at.slice(0,10)}`}
                              >
                                <Send className="w-3 h-3" />
                                Envoyée au comptable
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {f.type === 'artisan' ? (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              {f.projet_nom || 'Projet'}
                            </span>
                          ) : (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                              Entreprise
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {f.date_facture || f.date_validation || '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          <div className="font-medium text-slate-900">{formatMoney(f.montant_ht)}</div>
                          {f.montant_ttc > 0 && Math.abs(f.montant_ttc - (f.montant_ht || 0)) > 0.01 && (
                            <div className="text-xs text-slate-400" title="Montant TTC (TVA incluse)">
                              {formatMoney(f.montant_ttc)} TTC
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            urgent ? 'bg-red-100 text-red-700' : days > 15 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {days}j
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => openPdfPreview(
                                f.type === 'artisan'
                                  ? `/factures-artisans/${f.id}/download`
                                  : `/fournisseurs/factures-entreprise/${f.id}/download`,
                                {
                                  title: f.fichier_renomme || f.fournisseur_nom || 'Facture',
                                  filename: f.fichier_renomme || `facture-${f.id.slice(0,8)}.pdf`,
                                }
                              )}
                              className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                              title="Aperçu PDF"
                              data-testid={`facture-preview-btn-${f.type}-${f.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <a
                              href={f.type === 'artisan' ? getFactureDownloadUrl(f.id) : getFactureEntrepriseDownloadUrl(f.id)}
                              download={f.fichier_renomme || `facture-${f.id.slice(0,8)}.pdf`}
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="Télécharger la facture"
                              data-testid={`facture-download-btn-${f.type}-${f.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </a>
                            <button
                              onClick={() => openEditModal(f)}
                              className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                              title="Modifier"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handlePayFacture(f.type, f.id)}
                              className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded"
                              title="Marquer payée"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            {f.type === 'entreprise' && (
                              <button
                                onClick={() => handleDeleteFacture(f.id)}
                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab: Archives */}
      {activeTab === 'archives' && (
        <div>
          {archives.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <FolderArchive className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Aucune archive disponible</p>
              <p className="text-sm text-slate-400 mt-1">Les factures payées apparaîtront ici</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Global search across all months */}
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={archiveSearch}
                    onChange={(e) => setArchiveSearch(e.target.value)}
                    placeholder="Rechercher dans toutes les archives (fournisseur, montant, date)..."
                    className="w-full pl-9 pr-9 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    data-testid="archives-global-search"
                  />
                  {archiveSearch && (
                    <button
                      onClick={() => setArchiveSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"
                      title="Effacer la recherche"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {(() => {
                // Apply global search across months — both factures matching AND month_key matching
                const q = archiveSearch.trim().toLowerCase();
                const matchFacture = (f) => {
                  if (!q) return true;
                  const hay = `${f.nom || ''} ${f.categorie || ''} ${f.date_paiement || ''} ${f.montant_ht || ''} ${f.fichier_renomme || ''}`.toLowerCase();
                  return hay.includes(q);
                };
                const filteredArchives = q
                  ? archives
                      .map((archive) => {
                        const monthMatches = archive.month_key.toLowerCase().includes(q);
                        const factures = (archive.factures || []).filter(matchFacture);
                        if (monthMatches || factures.length > 0) {
                          return { ...archive, factures: monthMatches ? archive.factures : factures };
                        }
                        return null;
                      })
                      .filter(Boolean)
                  : archives;

                if (filteredArchives.length === 0) {
                  return (
                    <div className="text-center py-12 text-slate-500" data-testid="archives-empty-search">
                      <Search className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p>Aucune facture ne correspond à « {archiveSearch} »</p>
                    </div>
                  );
                }
                return filteredArchives.map((archive) => {
                const [year, month] = archive.month_key.split('-');
                const isExpanded = expandedArchive === archive.month_key;
                // Per-month search filter applied to expanded list
                const monthQ = (monthSearches[archive.month_key] || '').trim().toLowerCase();
                const visibleFactures = monthQ
                  ? (archive.factures || []).filter((f) => {
                      const hay = `${f.nom || ''} ${f.categorie || ''} ${f.date_paiement || ''} ${f.montant_ht || ''} ${f.fichier_renomme || ''}`.toLowerCase();
                      return hay.includes(monthQ);
                    })
                  : archive.factures;
                return (
                  <div key={archive.month_key} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    {/* Header - clickable */}
                    <div 
                      onClick={() => toggleArchive(archive.month_key)}
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${isExpanded ? 'bg-green-200' : 'bg-green-100'}`}>
                          {isExpanded ? (
                            <FolderOpen className="w-5 h-5 text-green-700" />
                          ) : (
                            <FolderArchive className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{archive.month_name}</h3>
                          <p className="text-sm text-slate-500">{archive.count} facture(s) • {formatMoney(archive.total_ht)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={getArchiveExportUrl(parseInt(year), parseInt(month))}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          ZIP
                        </a>
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded content - list of invoices */}
                    {isExpanded && archive.factures && archive.factures.length > 0 && (
                      <div className="border-t border-slate-200 bg-slate-50">
                        {/* Per-month search */}
                        <div className="px-4 py-3 bg-white border-b border-slate-200">
                          <div className="relative max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                            <input
                              type="text"
                              value={monthSearches[archive.month_key] || ''}
                              onChange={(e) => setMonthSearches({ ...monthSearches, [archive.month_key]: e.target.value })}
                              placeholder="Filtrer ce mois..."
                              className="w-full pl-8 pr-8 py-1.5 text-xs border border-slate-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`month-search-${archive.month_key}`}
                            />
                            {monthSearches[archive.month_key] && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const next = { ...monthSearches };
                                  delete next[archive.month_key];
                                  setMonthSearches(next);
                                }}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-700"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          {monthQ && (
                            <p className="text-xs text-slate-500 mt-1">
                              {visibleFactures.length} / {archive.factures.length} facture{archive.factures.length > 1 ? 's' : ''} affichée{visibleFactures.length > 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                        <table className="w-full text-sm">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-slate-500">Fournisseur</th>
                              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-slate-500">Type</th>
                              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-slate-500">Date paiement</th>
                              <th className="px-4 py-2 text-right text-xs font-bold uppercase text-slate-500">Montant HT</th>
                              <th className="px-4 py-2 text-center text-xs font-bold uppercase text-slate-500">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {visibleFactures.map((f) => (
                              <tr key={`${f.type}-${f.id}`} className="hover:bg-white">
                                <td className="px-4 py-3">
                                  <p className="font-medium text-slate-900">{f.nom}</p>
                                  {f.categorie && (
                                    <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                                      {f.categorie}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  {f.type === 'artisan' ? (
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Artisan</span>
                                  ) : (
                                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Entreprise</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-slate-500">{f.date_paiement || '-'}</td>
                                <td className="px-4 py-3 text-right font-mono">
                                  <div className="font-medium text-slate-900">{formatMoney(f.montant_ht)}</div>
                                  {f.montant_ttc > 0 && Math.abs(f.montant_ttc - (f.montant_ht || 0)) > 0.01 && (
                                    <div className="text-xs text-slate-400" title="Montant TTC (TVA incluse)">
                                      {formatMoney(f.montant_ttc)} TTC
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => openPdfPreview(
                                        f.type === 'artisan'
                                          ? `/factures-artisans/${f.id}/download`
                                          : `/fournisseurs/factures-entreprise/${f.id}/download`,
                                        {
                                          title: f.fichier_renomme || f.nom || 'Facture',
                                          filename: f.fichier_renomme || `facture-${f.id.slice(0,8)}.pdf`,
                                        }
                                      )}
                                      className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                                      title="Aperçu PDF"
                                      data-testid={`archive-preview-btn-${f.type}-${f.id}`}
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                    <a
                                      href={f.type === 'artisan' ? getFactureDownloadUrl(f.id) : getFactureEntrepriseDownloadUrl(f.id)}
                                      download={f.fichier_renomme || `facture-${f.id.slice(0,8)}.pdf`}
                                      className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                      title="Télécharger la facture"
                                      data-testid={`archive-download-btn-${f.type}-${f.id}`}
                                    >
                                      <Download className="w-4 h-4" />
                                    </a>
                                    <button
                                      onClick={() => openEditModal({...f, fournisseur_nom: f.nom})}
                                      className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                                      title="Modifier"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteArchivedFacture(f.type, f.id, f.nom)}
                                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                                      title="Supprimer"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              });
              })()}
            </div>
          )}
        </div>
      )}

      {/* Modal: Upload facture entreprise */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Ajouter une facture entreprise</h2>
                <p className="text-sm text-slate-500">
                  {!selectedFile ? "Glissez un PDF - l'IA analysera automatiquement" : "Vérifiez les données extraites"}
                </p>
              </div>
              <button onClick={closeModal} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Drop zone */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Fichier PDF * {selectedFile && <span className="text-green-600">✓ {selectedFile.name}</span>}
                </label>
                {!selectedFile ? (
                  <div 
                    onClick={() => !analyzingInvoice && fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                      analyzingInvoice 
                        ? 'border-indigo-400 bg-indigo-50' 
                        : isDragging
                          ? 'border-indigo-500 bg-indigo-100 scale-[1.02]'
                          : 'border-slate-300 hover:border-indigo-500 hover:bg-indigo-50'
                    }`}
                  >
                    {analyzingInvoice ? (
                      <>
                        <div className="w-10 h-10 mx-auto mb-3 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-indigo-600 font-medium">Analyse IA en cours...</p>
                      </>
                    ) : isDragging ? (
                      <>
                        <Upload className="w-10 h-10 mx-auto mb-3 text-indigo-500 animate-bounce" />
                        <p className="text-sm text-indigo-600 font-medium">Déposez le fichier ici</p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                        <p className="text-sm text-slate-600 font-medium">Glissez-déposez votre PDF ici</p>
                        <p className="text-xs text-slate-400 mt-2">ou cliquez pour sélectionner</p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <FileCheck className="w-6 h-6 text-green-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800">{selectedFile.name}</p>
                    </div>
                    <button onClick={() => { setSelectedFile(null); setNewFacture({...newFacture, fournisseur_nom: '', montant_ht: '', montant_ttc: ''}); }} className="p-1 text-slate-400 hover:text-red-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleAnalyzeInvoice(file);
                  }}
                />
              </div>

              {/* Form fields */}
              {(selectedFile || analyzingInvoice) && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-700 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Données extraites par l'IA - vérifiez et corrigez si nécessaire
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Fournisseur *</label>
                      <input
                        type="text"
                        value={newFacture.fournisseur_nom}
                        onChange={(e) => setNewFacture({ ...newFacture, fournisseur_nom: e.target.value })}
                        placeholder="Ex: Cabinet Dupont"
                        className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie *</label>
                      <select
                        value={newFacture.categorie}
                        onChange={(e) => setNewFacture({ ...newFacture, categorie: e.target.value })}
                        className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Montant HT *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newFacture.montant_ht}
                        onChange={(e) => setNewFacture({ ...newFacture, montant_ht: e.target.value })}
                        className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Montant TTC</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newFacture.montant_ttc}
                        onChange={(e) => setNewFacture({ ...newFacture, montant_ttc: e.target.value })}
                        className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                      <input
                        type="date"
                        value={newFacture.date_facture}
                        onChange={(e) => setNewFacture({ ...newFacture, date_facture: e.target.value })}
                        className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">N° Facture</label>
                    <input
                      type="text"
                      value={newFacture.numero_facture}
                      onChange={(e) => setNewFacture({ ...newFacture, numero_facture: e.target.value })}
                      className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Preview renamed filename */}
                  {newFacture.fournisseur_nom && newFacture.montant_ht && (
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Nom du fichier renommé :</p>
                      <p className="text-sm font-mono text-indigo-600">
                        {newFacture.categorie?.replace(/[^a-zA-Z0-9]/g, '_')}_{newFacture.fournisseur_nom.replace(/[^a-zA-Z0-9]/g, '_')}_{Math.round(parseFloat(newFacture.montant_ht) || 0)}EUR.pdf
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 bg-slate-50 rounded-b-xl flex gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="flex-1 h-10 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-white transition-colors"
              >
                Annuler
              </button>
              {selectedFile && (
                <button
                  type="button"
                  onClick={handleSaveFacture}
                  disabled={uploadingFacture || !newFacture.fournisseur_nom || !newFacture.montant_ht}
                  className="flex-1 h-10 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploadingFacture ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    'Enregistrer'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Edit facture */}
      {showEditModal && editingFacture && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Modifier la facture</h2>
                <p className="text-sm text-slate-500">
                  {editingFacture.type === 'entreprise' ? 'Facture entreprise' : 'Facture artisan'}
                </p>
              </div>
              <button 
                onClick={() => { setShowEditModal(false); setEditingFacture(null); }} 
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {editingFacture.type === 'entreprise' ? 'Fournisseur' : 'Artisan'} *
                  </label>
                  <input
                    type="text"
                    value={editingFacture.fournisseur_nom}
                    onChange={(e) => setEditingFacture({ ...editingFacture, fournisseur_nom: e.target.value })}
                    className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                
                {editingFacture.type === 'entreprise' && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie</label>
                    <select
                      value={editingFacture.categorie}
                      onChange={(e) => setEditingFacture({ ...editingFacture, categorie: e.target.value })}
                      className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Montant HT *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingFacture.montant_ht}
                    onChange={(e) => setEditingFacture({ ...editingFacture, montant_ht: e.target.value })}
                    className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Montant TTC</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingFacture.montant_ttc}
                    onChange={(e) => setEditingFacture({ ...editingFacture, montant_ttc: e.target.value })}
                    className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date facture</label>
                  <input
                    type="date"
                    value={editingFacture.date_facture}
                    onChange={(e) => setEditingFacture({ ...editingFacture, date_facture: e.target.value })}
                    className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">N° Facture</label>
                  <input
                    type="text"
                    value={editingFacture.numero_facture}
                    onChange={(e) => setEditingFacture({ ...editingFacture, numero_facture: e.target.value })}
                    className="w-full h-10 px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 bg-slate-50 rounded-b-xl flex gap-3">
              <button
                type="button"
                onClick={() => { setShowEditModal(false); setEditingFacture(null); }}
                className="flex-1 h-10 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-white transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={savingEdit || !editingFacture.fournisseur_nom || !editingFacture.montant_ht}
                className="flex-1 h-10 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {savingEdit ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Enregistrer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal (aperçu + téléchargement avec le nom original) */}
      {pdfPreview && (
        <PDFPreviewModal
          url={pdfPreview.url}
          title={pdfPreview.title}
          downloadFilename={pdfPreview.filename}
          onClose={closePdfPreview}
        />
      )}
    </div>
  );
};

export default FournisseursPage;
